import React from 'react';
import clsx from 'clsx';
import { Button } from '../Button';

import { useNavigate } from 'react-router';

import { Tabs as TabsUI, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';

export interface Tab {
  title: string;
  contents?: React.ReactNode;
}

export interface TabProps {
  tabs: Tab[];
  loading?: boolean;
  error?: Error | undefined;
  value?: string;
  onValueChange?: (value: string) => void;
}

export const Tabs = ({ tabs, error, value, onValueChange }: TabProps) => {
  const navigate = useNavigate();

  // refresh
  const handleReload = () => {
    navigate(0);
  };

  const controlled = value !== undefined && onValueChange !== undefined;

  return (
    <>
      {error ? (
        <div className="flex flex-col justify-center items-center mx-auto w-full max-w-lg text-center min-h-[300px]">
          <span className="text-8xl material-symbols-outlined dark:text-blue-100">error</span>
          <p className="mb-8 text-xl dark:text-blue-100">
            We are unable to retrieve the latest data. Please try again later or contact support if
            this issue persists.
          </p>

          <Button onClick={handleReload}>Reload Page</Button>
        </div>
      ) : (
        <TabsUI
          {...(controlled
            ? { value, onValueChange }
            : { defaultValue: tabs[0].title })}
        >
          {/* The wrapper owns the full-width underline; the list overlaps it by 2px so
              active-tab borders paint over it without children overflowing the scroll box */}
          <div className="mb-14 border-b-2 dark:border-blue-900 border-grey-400">
            <TabsList className="flex justify-start p-0 -mb-[2px] w-full overflow-x-auto bg-transparent rounded-none">
              {tabs.map((tab: Tab, i: number) => {
                return (
                  <TabsTrigger
                    key={tab.title + i}
                    value={tab.title}
                    className={clsx(
                      `px-2.5 h-full !border-b-2 border-b-grey-400 rounded-b-none rounded-t-md cursor-pointer transition-colors text-grey-1000 dark:border-blue-900  mr-0 border-0 max-w-[200px] shrink-0 whitespace-nowrap  dark:text-blue-100  `,
                      `hover:!bg-blue-200 dark:hover:!bg-blue-900 hover:border-b-2   hover:border-b-blue-800 `,
                      ` data-[state=active]:bg-transparent data-[state=active]:border-blue-800   data-[state=active]:font-bold`
                    )}
                  >
                    {tab.title}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {tabs.map((tab: Tab, i: number) => {
            return (
              <TabsContent
                key={tab.title + i}
                value={tab.title}
                className="w-full text-grey-1000 dark:text-blue-100"
              >
                <div className="flex flex-col gap-12">{tab.contents}</div>
              </TabsContent>
            );
          })}
        </TabsUI>
      )}
    </>
  );
};
